import { withWorkroomReadCompatibility } from "./workroomCompatibility.ts";
import * as Schema from "effect/Schema";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

// Arc RM (2026-09-03): the room index of a World — rooms and sessions folded from
// the World's one move ledger (backend/convex/lib/worldEntry.js). A room is a
// World-owned group of sessions sharing one context scope (kind general |
// workroom); a session is one agent conversation whose parent is a room or
// another session; a session's roomId is DERIVED by walking parentId, never
// stored twice. Register of record: GOALS/GOALS-ROOMS.html.

export const WORLD_ROOM_INDEX_SCHEMA_VERSION = "world-room-index-v1";
export const HARMONY_ROOT_ROOM_ID = "R_harmony";
export const HARMONY_FIRST_SESSION_ID = "S_harmony_session";
export const HARMONY_ROOT_GOAL_ID = "goal-000";
export const ROOM_ID_PATTERN = /^R_[A-Za-z0-9._:-]{1,253}$/u;
export const SESSION_ID_PATTERN = /^S_[A-Za-z0-9._:-]{1,253}$/u;

export const RoomId = TrimmedNonEmptyString.check(Schema.isPattern(ROOM_ID_PATTERN));
export type RoomId = typeof RoomId.Type;
export const SessionId = TrimmedNonEmptyString.check(Schema.isPattern(SESSION_ID_PATTERN));
export type SessionId = typeof SessionId.Type;

export const ROOM_KINDS = ["general", "workroom"] as const;
export const RoomKind = Schema.Literals(ROOM_KINDS);
export type RoomKind = typeof RoomKind.Type;

export const WorldRoomGoalProjection = Schema.Struct({
  goalId: TrimmedNonEmptyString,
  parentGoalId: Schema.NullOr(TrimmedNonEmptyString),
  title: TrimmedNonEmptyString,
  statement: TrimmedNonEmptyString,
  cleoCan: Schema.NullOr(Schema.String),
  storedState: Schema.Literals(["open", "closed"]),
  storedStep: Schema.Literals(["comprehend", "conceive", "decide", "design", "build", "run"]),
});
export type WorldRoomGoalProjection = typeof WorldRoomGoalProjection.Type;

const Sha256Hex = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/u));
export const WorldSessionSelectionBirth = Schema.Struct({
  schemaVersion: Schema.Literal("world-session-selection-v1"),
  roomId: RoomId,
  sessionId: SessionId,
  agentEnvironmentId: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  agentThreadId: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  selectionCut: NonNegativeInt,
  selectionDigest: Sha256Hex,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type WorldSessionSelectionBirth = typeof WorldSessionSelectionBirth.Type;

export const WorldRoomProjection = withWorkroomReadCompatibility(
  Schema.Struct({
    /** Older wire projections predate reversible room archival. */
    archived: Schema.optional(Schema.Boolean),
    archiveSeq: Schema.optional(NonNegativeInt),
    roomId: RoomId,
    kind: RoomKind,
    workroomKey: Schema.NullOr(TrimmedNonEmptyString),
    label: TrimmedNonEmptyString,
    bornSeq: NonNegativeInt,
    goalId: Schema.NullOr(TrimmedNonEmptyString),
    goalIds: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
    goals: Schema.optional(Schema.Array(WorldRoomGoalProjection)),
    focusSeq: Schema.optional(NonNegativeInt),
    contribution: Schema.optional(Schema.String),
    state: Schema.Literal("ready"),
    defect: Schema.Null,
    goal: Schema.NullOr(WorldRoomGoalProjection),
  }),
);
export type WorldRoomProjection = typeof WorldRoomProjection.Type;

export const WorldSessionParticipant = Schema.Struct({
  threadId: TrimmedNonEmptyString,
  environmentId: TrimmedNonEmptyString,
});
export const WorldSessionExecution = Schema.Struct({
  ...WorldSessionParticipant.fields,
  executionId: TrimmedNonEmptyString,
  resolutionDigest: Sha256Hex,
  startedSeq: NonNegativeInt,
});
export const WorldSessionTranscript = Schema.Struct({
  threadId: TrimmedNonEmptyString,
  snapshotId: TrimmedNonEmptyString,
  transcriptMemberId: TrimmedNonEmptyString,
  contentSha256: Sha256Hex,
  bytes: NonNegativeInt,
  snapshotSequence: NonNegativeInt,
});
export const WorldSessionRetirement = Schema.Struct({
  operationId: TrimmedNonEmptyString,
  roomId: RoomId,
  fence: NonNegativeInt,
});
const WorldSessionLifecycleFields = {
  // Absent only on an older wire projection; absence never proves coverage.
  lifecycle: Schema.optional(Schema.Literals(["open", "retired"])),
  executionCoverageVersion: Schema.optional(
    Schema.Union([Schema.Literal(1), Schema.Literal("unknown")]),
  ),
  retirement: Schema.optional(Schema.NullOr(WorldSessionRetirement)),
  retiredSeq: Schema.optional(Schema.NullOr(NonNegativeInt)),
  participants: Schema.optional(
    Schema.Array(WorldSessionParticipant).check(Schema.isMaxLength(128)),
  ),
  unresolvedExecutions: Schema.optional(
    Schema.Array(WorldSessionExecution).check(Schema.isMaxLength(128)),
  ),
  transcripts: Schema.optional(Schema.Array(WorldSessionTranscript).check(Schema.isMaxLength(128))),
  /** Threads whose execution was closed as lost, with no transcript (W-d958415c). */
  lostThreads: Schema.optional(Schema.Array(TrimmedNonEmptyString).check(Schema.isMaxLength(128))),
};

export const WorldSessionForkedFrom = Schema.Struct({
  sessionId: SessionId,
  threadId: TrimmedNonEmptyString,
  snapshotRef: TrimmedNonEmptyString,
});
export type WorldSessionForkedFrom = typeof WorldSessionForkedFrom.Type;

export const WorldSessionProjection = Schema.Struct({
  sessionId: SessionId,
  /** A room id or a session id — never null: every session hangs under a live parent. */
  parentId: TrimmedNonEmptyString,
  /** Derived by the fold from the parent chain; the room this session belongs to. */
  roomId: RoomId,
  label: TrimmedNonEmptyString,
  bornSeq: NonNegativeInt,
  engagement: Schema.Literals(["headless", "engaged"]),
  goalId: Schema.NullOr(TrimmedNonEmptyString),
  goalIds: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  goals: Schema.optional(Schema.Array(WorldRoomGoalProjection)),
  focusSeq: Schema.optional(NonNegativeInt),
  contribution: Schema.optional(Schema.String),
  state: Schema.Literal("ready"),
  defect: Schema.Null,
  goal: Schema.NullOr(WorldRoomGoalProjection),
  assignment: Schema.optional(Schema.String),
  forkedFrom: Schema.optional(WorldSessionForkedFrom),
  ...WorldSessionLifecycleFields,
});
export type WorldSessionProjection = typeof WorldSessionProjection.Type;

function roomArchiveIssue(room: WorldRoomProjection, cut: number): string | undefined {
  if (room.archived === undefined && room.archiveSeq === undefined) return undefined;
  if (
    room.archived === undefined ||
    room.archiveSeq === undefined ||
    room.archiveSeq > cut ||
    (room.archiveSeq !== 0 && room.archiveSeq <= room.bornSeq) ||
    (room.archived && room.archiveSeq === 0)
  )
    return "Room archive state must name a valid transition at this cut.";
  return undefined;
}

function goalProjectionIssue(
  record: WorldRoomProjection | WorldSessionProjection,
  cut: number,
): string | undefined {
  if (record.goal?.goalId !== (record.goalId ?? undefined))
    return "The primary goal must match its projection.";
  if (record.goalIds !== undefined) {
    if (
      record.goalIds.length > 64 ||
      new Set(record.goalIds).size !== record.goalIds.length ||
      (record.goalIds[0] ?? null) !== record.goalId ||
      record.goals === undefined ||
      record.goals.length !== record.goalIds.length ||
      record.goals.some((goal, i) => goal.goalId !== record.goalIds![i])
    )
      return "An explicit goal selection must match all projected goals, including an empty selection.";
  } else if (record.goalId === null || record.goals !== undefined) {
    return "An unbound record requires an explicit empty goal selection.";
  }
  if (record.focusSeq !== undefined && record.focusSeq > cut)
    return "Room focus cannot be newer than its read cut.";
  return undefined;
}

function sessionLifecycleIssue(session: WorldSessionProjection, cut: number): string | undefined {
  const hasLifecycle = Object.keys(WorldSessionLifecycleFields).some((key) =>
    Object.hasOwn(session, key),
  );
  if (hasLifecycle && session.lifecycle === undefined)
    return "Session execution facts require an explicit lifecycle.";
  if (session.lifecycle !== undefined) {
    if (
      session.executionCoverageVersion === undefined ||
      session.retirement === undefined ||
      session.retiredSeq === undefined ||
      session.participants === undefined ||
      session.unresolvedExecutions === undefined ||
      session.transcripts === undefined
    )
      return "A session lifecycle projection must contain every execution and retirement fact.";
    if (
      session.retirement !== null &&
      (session.retirement.roomId !== session.roomId ||
        session.retirement.fence <= session.bornSeq ||
        session.retirement.fence > cut ||
        session.executionCoverageVersion !== 1)
    )
      return "A retirement fence must belong to this session room and cut.";
    if (
      session.lifecycle === "retired" &&
      (session.retiredSeq === null ||
        session.retiredSeq > cut ||
        session.retirement === null ||
        session.retiredSeq <= session.retirement.fence ||
        session.unresolvedExecutions.length !== 0 ||
        session.executionCoverageVersion !== 1)
    )
      return "A retired session requires its fence, retirement sequence and no unresolved executions.";
    if (
      session.lifecycle === "retired" &&
      session.participants.some(
        (participant) =>
          !session.transcripts!.some((row) => row.threadId === participant.threadId) &&
          !(session.lostThreads ?? []).includes(participant.threadId),
      )
    )
      return "Retired session snapshots must cover every participant not closed as lost.";
    if (session.lifecycle === "open" && session.retiredSeq !== null)
      return "An open session cannot have a retirement sequence.";
    const participants = new Set(session.participants.map((row) => row.threadId));
    if (
      participants.size !== session.participants.length ||
      new Set(session.unresolvedExecutions.map((row) => row.executionId)).size !==
        session.unresolvedExecutions.length ||
      new Set(session.transcripts.map((row) => row.threadId)).size !== session.transcripts.length
    )
      return "Session participant, execution and snapshot identities must be unique.";
    if (
      session.unresolvedExecutions.some(
        (row) => !participants.has(row.threadId) || row.startedSeq > cut,
      ) ||
      session.transcripts.some(
        (row) =>
          (session.lifecycle !== "retired" && !participants.has(row.threadId)) ||
          row.bytes > 4 * 1024 * 1024 ||
          row.transcriptMemberId !==
            `world-r2:context/sessions/${session.roomId}/${session.sessionId}/${row.threadId}/snapshots/${row.snapshotId}.md`,
      )
    )
      return "Execution and snapshot facts must belong to this session's participants and cut.";
  }
  return undefined;
}

/** Exact authorized readback of one coordinate; never a complete World index. */
export const WorldRoomEntry = Schema.Struct({
  schemaVersion: Schema.Literal("world-room-entry-v1"),
  worldKey: TrimmedNonEmptyString,
  head: NonNegativeInt,
  cut: NonNegativeInt,
  rootRoomId: Schema.Literal(HARMONY_ROOT_ROOM_ID),
  room: WorldRoomProjection,
  session: WorldSessionProjection,
}).check(
  Schema.makeFilter((entry) => {
    if (
      entry.cut > entry.head ||
      entry.room.bornSeq > entry.cut ||
      entry.session.bornSeq > entry.cut
    )
      return "The entry must exist at its readback cut.";
    const recordIssue =
      roomArchiveIssue(entry.room, entry.cut) ??
      goalProjectionIssue(entry.room, entry.cut) ??
      goalProjectionIssue(entry.session, entry.cut) ??
      sessionLifecycleIssue(entry.session, entry.cut);
    if (recordIssue !== undefined) return recordIssue;
    if (
      entry.room.roomId === HARMONY_ROOT_ROOM_ID &&
      (entry.room.kind !== "general" ||
        entry.room.goalId !== HARMONY_ROOT_GOAL_ID ||
        entry.room.bornSeq !== 1)
    )
      return "The entry must preserve the canonical Harmony room.";
    if (entry.room.roomId !== entry.session.roomId)
      return "The session must belong to the readback room.";
    if (entry.session.parentId.startsWith("R_") && entry.session.parentId !== entry.room.roomId)
      return "A root session must hang under its readback room.";
    return undefined;
  }),
);
export type WorldRoomEntry = typeof WorldRoomEntry.Type;

/**
 * Can 80 (the principal, 17 Oct 2026: "the quantity of sessions is
 * overwhelming"): when each named session was last active in its World, so the
 * Desk can fold quiet sessions it never talked to (made on Pocket or another
 * device). activeAt is ms since the epoch: the time of the session's latest
 * lifecycle move at the cut (an execution, a retirement step), else of its
 * birth, folded from the ledger (backend/convex/lib/worldRoomIndex.js
 * readWorldSessionTimes). A session not live at the cut, or in a room the
 * reader may not see, is left out: absence says nothing about its age.
 */
export const WORLD_SESSION_TIMES_SCHEMA_VERSION = "world-session-times-v1";
export const WORLD_SESSION_TIMES_MAX = 64;
export const WorldSessionTimes = Schema.Struct({
  schemaVersion: Schema.Literal(WORLD_SESSION_TIMES_SCHEMA_VERSION),
  worldKey: TrimmedNonEmptyString,
  generation: NonNegativeInt,
  cut: NonNegativeInt,
  sessions: Schema.Array(
    Schema.Struct({
      sessionId: SessionId,
      activeAt: NonNegativeInt,
      /** Whether the reader opened it (its record's author). Absent on older readbacks. */
      openedByReader: Schema.optional(Schema.Boolean),
      /**
       * Retired: its latest lifecycle fact is its accepted retirement, which
       * is final. False says only that this read did not find it retired
       * (its entry is the authority for an open session). Absent on older
       * readbacks.
       */
      retired: Schema.optional(Schema.Boolean),
    }),
  ).check(Schema.isMaxLength(WORLD_SESSION_TIMES_MAX)),
});
export type WorldSessionTimes = typeof WorldSessionTimes.Type;

/**
 * One bounded page of a room's live sessions in birth order
 * (backend/convex/lib/worldRoomIndex.js readSessionChildrenPage): the first
 * page carries the room as `parent`; follow continueCursor until it is null.
 * A room the reader may not see answers parentId null and no sessions.
 */
export const WorldSessionChildrenPage = Schema.Struct({
  schemaVersion: Schema.Literal("world-session-children-page-v1"),
  worldKey: TrimmedNonEmptyString,
  generation: NonNegativeInt,
  head: NonNegativeInt,
  cut: NonNegativeInt,
  parentId: Schema.NullOr(TrimmedNonEmptyString),
  parent: Schema.NullOr(WorldRoomProjection),
  sessions: Schema.Array(WorldSessionProjection),
  continueCursor: Schema.NullOr(Schema.String),
});
export type WorldSessionChildrenPage = typeof WorldSessionChildrenPage.Type;

/** A page is never a complete directory. Cross-page ancestry and Harmony are
 * validated with WorldRoomIndex only after the final continuation is consumed.
 *
 * The Desk reads its pages under a grant through its Mac server, and the World
 * names that grant and the directory generation on every page; the server
 * refuses a page whose grant is not the one it read under. A page the person
 * reads with their own sign-in (Pocket, cleo-web: backend boards.readRooms)
 * names the person's Actor and no grant, and the generation only as its
 * continuation cursor carries it, so both are optional here; the reader pins
 * whichever the page names across the whole read. */
export const WorldRoomIndexPage = Schema.Struct({
  schemaVersion: Schema.Literal("world-room-index-page-v1"),
  worldKey: TrimmedNonEmptyString,
  actorId: TrimmedNonEmptyString,
  grantId: Schema.optional(TrimmedNonEmptyString),
  generation: Schema.optional(NonNegativeInt.check(Schema.isGreaterThan(0))),
  head: NonNegativeInt,
  cut: NonNegativeInt,
  /**
   * The World's Goal catalog revision when the page was read. Goals are read
   * live into room rows and a Goal insert appends no move, so head and cut
   * alone do not say the rows are unchanged (W-35fbf22c). Absent from a World
   * that does not send it yet.
   */
  goalCatalogRevision: Schema.optional(NonNegativeInt),
  rootRoomId: Schema.Literal(HARMONY_ROOT_ROOM_ID),
  rooms: Schema.Array(WorldRoomProjection),
  sessions: Schema.Array(WorldSessionProjection),
  continueCursor: Schema.NullOr(TrimmedNonEmptyString.check(Schema.isMaxLength(2048))),
}).check(
  Schema.makeFilter((page) => {
    if (page.cut > page.head) return "Room page cut cannot exceed its ledger head.";
    if (page.rooms.length + page.sessions.length > 64)
      return "A projected room page contains at most 64 records.";
    if (
      new Set(page.rooms.map((room) => room.roomId)).size !== page.rooms.length ||
      new Set(page.sessions.map((session) => session.sessionId)).size !== page.sessions.length
    )
      return "Room page identities must be unique.";
    for (const record of [...page.rooms, ...page.sessions]) {
      if (record.bornSeq > page.cut) return "A room page cannot expose a record after its cut.";
      const issue = goalProjectionIssue(record, page.cut);
      if (issue !== undefined) return issue;
    }
    for (const room of page.rooms) {
      const issue = roomArchiveIssue(room, page.cut);
      if (issue !== undefined) return issue;
    }
    for (const session of page.sessions) {
      const issue = sessionLifecycleIssue(session, page.cut);
      if (issue !== undefined) return issue;
    }
    return true;
  }),
);
export type WorldRoomIndexPage = typeof WorldRoomIndexPage.Type;

export const WorldRoomIndex = Schema.Struct({
  schemaVersion: Schema.Literal(WORLD_ROOM_INDEX_SCHEMA_VERSION),
  worldKey: TrimmedNonEmptyString,
  head: NonNegativeInt,
  cut: NonNegativeInt,
  rootRoomId: Schema.Literal(HARMONY_ROOT_ROOM_ID),
  rooms: Schema.Array(WorldRoomProjection),
  sessions: Schema.Array(WorldSessionProjection),
}).check(
  Schema.makeFilter((index) => {
    if (index.cut > index.head) return "Room index cut cannot exceed its ledger head.";
    const roomIds = new Set(index.rooms.map((room) => room.roomId));
    if (roomIds.size !== index.rooms.length) return "Room identities must be unique.";
    const sessionIds = new Set(index.sessions.map((session) => session.sessionId));
    if (sessionIds.size !== index.sessions.length)
      return "World session identities must be unique.";
    const root = index.rooms.find((room) => room.roomId === index.rootRoomId);
    if (
      root === undefined ||
      root.kind !== "general" ||
      root.goalId !== HARMONY_ROOT_GOAL_ID ||
      root.bornSeq !== 1
    ) {
      return "The room index must contain the canonical R_harmony general room at sequence 1.";
    }
    const workroomKeys = index.rooms
      .filter((room) => room.kind === "workroom")
      .map((room) => room.workroomKey);
    if (new Set(workroomKeys).size !== workroomKeys.length)
      return "A workroom is 0..1 per World and workroomKey.";
    if (
      index.rooms.some((room) => room.bornSeq > index.cut) ||
      index.sessions.some((session) => session.bornSeq > index.cut)
    ) {
      return "The room index cannot expose a record born after its cut.";
    }
    for (const room of index.rooms) {
      const archiveIssue = roomArchiveIssue(room, index.cut);
      if (archiveIssue !== undefined) return archiveIssue;
    }
    for (const record of [...index.rooms, ...index.sessions]) {
      const goalIssue = goalProjectionIssue(record, index.cut);
      if (goalIssue !== undefined) return goalIssue;
    }
    const parentOf = new Map(
      index.sessions.map((session) => [session.sessionId, session.parentId]),
    );
    for (const session of index.sessions) {
      const lifecycleIssue = sessionLifecycleIssue(session, index.cut);
      if (lifecycleIssue !== undefined) return lifecycleIssue;
      if (!roomIds.has(session.parentId) && !sessionIds.has(session.parentId)) {
        return `Session '${session.sessionId}' names a parent absent from the index.`;
      }
      let cursor: string | undefined = session.sessionId;
      const seen = new Set<string>();
      while (cursor !== undefined && !roomIds.has(cursor)) {
        if (seen.has(cursor))
          return `Session '${session.sessionId}' sits on a cyclic parent chain.`;
        seen.add(cursor);
        cursor = parentOf.get(cursor);
      }
      if (cursor !== session.roomId)
        return `Session '${session.sessionId}' names roomId '${session.roomId}' but its chain reaches '${String(cursor)}'.`;
    }
    return true;
  }),
);
export type WorldRoomIndex = typeof WorldRoomIndex.Type;

/** Shared read-only shape for an exact index or a rendered directory of admitted entries. */
export type WorldRoomDirectory = Pick<
  WorldRoomIndex,
  "worldKey" | "rootRoomId" | "rooms" | "sessions"
>;

/** The room a session belongs to, read from the index — the fold's answer, never recomputed elsewhere. */
export const roomOfSession = (
  index: WorldRoomDirectory,
  sessionId: string,
): WorldRoomProjection | undefined => {
  const session = index.sessions.find((candidate) => candidate.sessionId === sessionId);
  return session === undefined
    ? undefined
    : index.rooms.find((room) => room.roomId === session.roomId);
};

/** The sessions whose parent IS the room (its root sessions), in born order. */
export const rootSessionsOfRoom = (
  index: WorldRoomDirectory,
  roomId: string,
): ReadonlyArray<WorldSessionProjection> =>
  index.sessions
    .filter((session) => session.parentId === roomId)
    .sort((a, b) => a.bornSeq - b.bornSeq);

/** Where a World's entry lands: the standing general room's first-born root session. */
export const entrySessionOf = (index: WorldRoomDirectory): WorldSessionProjection | undefined =>
  rootSessionsOfRoom(index, index.rootRoomId)[0];

/** A ROOT session hangs directly under its room (its parent is an R_ id); every other session is nested. */
export const isRootSession = (session: Pick<WorldSessionProjection, "parentId">): boolean =>
  ROOM_ID_PATTERN.test(session.parentId);
